// Deterministic gameplay math for #28.
//
// Portability status, precisely: ECMA-262 permits implementation-approximated
// results for Math.hypot, Math.sin, Math.cos AND Math.sqrt alike, so none of
// them proves cross-engine identity on its own. What this module guarantees:
// - detHypot/detDistance use one fixed operation sequence (abs, max, /, *,
//   +, sqrt) over finite gameplay magnitudes (world <= 2048px, radii <=
//   detection range). Parity between Bun and Chromium is ESTABLISHED BY the
//   cross-engine checkpoint run (/tmp/razmak-p1/p2-determinism-progress.md),
//   not by a spec citation; the artifact records the tested engines.
// - Firing-arc trig uses pinned literals (below): decimal-to-double parsing
//   and integer slot lookup are exactly specified, and the remaining runtime
//   ops (+,-,*) are exactly specified IEEE ops, so both engines consume
//   bit-identical candidate angles by construction. The literals were
//   generated once from the host libm and are shared verbatim.
// This module stays a narrow gameplay helper, not a numerical framework:
// distance for movement/combat/AI, one 85-slot arc table for firing.
import type { Point } from "./map-data";

// Shared position lattice, including mirrored half-step ties.
export function snapPosition(v: number): number {
  const z = v * 256;
  const r = Math.round(z);
  return (r - z === 0.5 && r % 2 !== 0 ? r - 1 : r) / 256;
}

// Euclidean length without Math.hypot (whose libm implementation varies most
// between engines). Single-ratio scaling — factor out the largest magnitude
// so the larger component divides to exactly 1, leaving a single rounded
// ratio — keeps overflow/underflow classification close to hypot with the
// minimum rounding steps.
// Matches hypot(±Infinity, anything) = Infinity, including NaN.
export function detHypot(dx: number, dy: number): number {
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);
  // hypot(±Infinity, anything) is +Infinity, even for NaN.
  if (ax === Infinity || ay === Infinity) return Infinity;
  const m = Math.max(ax, ay);
  if (m === 0) return 0;
  if (Number.isNaN(m)) return NaN;
  const r = Math.min(ax, ay) / m;
  return m * Math.sqrt(1 + r * r);
}

export function detDistance(a: Point, b: Point): number {
  return detHypot(a.x - b.x, a.y - b.y);
}

// Firing-arc candidate angles for firingPosition in ./sim, which samples
// phi = m * (24 / (range - 8)) * sgn for m = 0..16 over the five weapon
// ranges below (32 candidates per shot). 81 of the 85 (range, m) pairs are
// distinct; all are pinned here as [sin, cos] of the non-negative base angle
// so no engine ever evaluates a transcendental at runtime. The caller applies
// the candidate sign to sin (exact sign flip; cos is even; m = 0 is exactly
// zero and skips the flip so -0 can never leak into a coordinate).
//
// Regen (adding a weapon range requires it; an unknown range throws loudly
// in firingTrig instead of silently changing ballistics):
//   bun -e 'const R=[130,150,170,190,280],o=[];for(const r of R)for(let m=0;m<=16;m++){const t=m*24/(r-8);o.push(JSON.stringify(Math.sin(t))+", "+JSON.stringify(Math.cos(t)))};console.log(o.join(", "))'
// Generated with Bun 1.4.2 on 2026-10-12; values are that libm's results
// shared verbatim, so both engines use the identical doubles.
const FIRING_RANGES = [130, 150, 170, 190, 280];
const FIRING_ARC: readonly (readonly [number, number])[] = [
  [0, 1],
  [0.19545493568339475, 0.9807126837749168],
  [0.38337026906223165, 0.923594736234],
  [0.5564972352196716, 0.8308494612099484],
  [0.708157525068979, 0.7060544735983042],
  [0.832500898671931, 0.5540236941778097],
  [0.9247308560941802, 0.380621654385723],
  [0.9812896606272686, 0.19253727417313266],
  [0.9999955771745117, -0.0029741606236431454],
  [0.980127031880456, -0.19837086826751416],
  [0.9224504465771394, -0.38611549257914546],
  [0.8291906743436176, -0.5589658536812214],
  [0.7039451766161855, -0.7102543124253505],
  [0.5515452524357163, -0.8341449721215264],
  [0.3778696728629054, -0.9258587961079598],
  [0.18961790954534302, -0.9818579573337755],
  [-0.005948294938899466, -0.9999823087371695],
  [0, 1],
  [0.1682105637079989, 0.9857510873729926],
  [0.33162749216556786, 0.9434104125140745],
  [0.4855937583019754, 0.8741845925765123],
  [0.6257216583698528, 0.7800464128799525],
  [0.7480178519598538, 0.6636786068191186],
  [0.8489971635178194, 0.5283974037963255],
  [0.9257819022686998, 0.3780580238954696],
  [0.9761838701453975, 0.21694481249376243],
  [0.9987667206749037, 0.049649145735843],
  [0.992886891729091, -0.11906141370126759],
  [0.9587119456457807, -0.2843789817762235],
  [0.8972157940665204, -0.44159236732260587],
  [0.81015094357281, -0.5862213307515222],
  [0.6999985530597869, -0.7141442611365051],
  [0.5698977261036022, -0.8217156331614627],
  [0.4235560533362572, -0.9058698966640926],
  [0, 1],
  [0.14760681973027226, 0.9890461196370546],
  [0.291979904572384, 0.9564244535382298],
  [0.42995636352835553, 0.9028496693588987],
  [0.5585134413495737, 0.8294954706518031],
  [0.6748347403355116, 0.7379688836504577],
  [0.7763719213006605, 0.6302750509229451],
  [0.8609005319796542, 0.5087733031883142],
  [0.9265687397952457, 0.3761254716637125],
  [0.9719379013633127, 0.23523757330298942],
  [0.9960140799478878, 0.08919614647260522],
  [0.9982698203893463, -0.05879936819237232],
  [0.978655704465837, -0.2055067203681578],
  [0.9376014335358621, -0.34771188048655516],
  [0.8760064147437312, -0.4822994519257035],
  [0.7952200570230491, -0.6063209223738354],
  [0.6970122085686771, -0.7170592591314996],
  [0, 1],
  [0.1314862837090213, 0.9913179899489319],
  [0.260689436944564, 0.9654227143927815],
  [0.3853659735565867, 0.922763819416856],
  [0.5033510076570936, 0.8640820349310524],
  [0.6125958447422123, 0.7903963126208113],
  [0.7112035552647422, 0.7029861328495682],
  [0.7974619129569445, 0.6033692877360023],
  [0.8698731259618745, 0.49327552618117687],
  [0.9271798445212944, 0.3746165184738498],
  [0.9683869936221513, 0.24945266200915056],
  [0.992779051499106, 0.11995730450679308],
  [0.9999324739688505, -0.011620994042418424],
  [0.9897230488598214, -0.14299750541747056],
  [0.9623280528348429, -0.27189100523389864],
  [0.9182231731555907, -0.3960633841698551],
  [0.8581742478394175, -0.5133585105413662],
  [0, 1],
  [0.08812084651448769, 0.9961097913430879],
  [0.1755560760690452, 0.9844694328191402],
  [0.26162540608980805, 0.9651694912751558],
  [0.3456591812712935, 0.9383601283104792],
  [0.42700358377413344, 0.9042499319568935],
  [0.5050257202007122, 0.8631042937766861],
  [0.5791185457699147, 0.8152433440055424],
  [0.6487055873788524, 0.761039460805718],
  [0.713245428804173, 0.7009143730085375],
  [0.7722359231462198, 0.6353358788880932],
  [0.8252180997414627, 0.5648142064954541],
  [0.8717797351457959, 0.48989804387150393],
  [0.9115585604049596, 0.4111702700250074],
  [0.9442450796581842, 0.3292434198906783],
  [0.9695849781451427, 0.24475491855176926],
  [0.9873811008809169, 0.15836212180691622],
];

export function firingTrig(
  range: number,
  m: number,
  sgn: 1 | -1,
  allowedRanges: readonly number[] = FIRING_RANGES,
): { sin: number; cos: number } {
  // Exact integer slot match; indexOf on five elements is deterministic.
  const slot = (FIRING_RANGES as readonly number[]).indexOf(range);
  if (
    !Number.isFinite(range) ||
    range < 32 ||
    range > 8192 ||
    !Number.isInteger(m) ||
    m < 0 ||
    m > 16 ||
    (sgn !== 1 && sgn !== -1)
  ) {
    throw new RangeError(
      `firingTrig: unsupported (range, m) = (${String(range)}, ${String(m)}); regen FIRING_ARC.`,
    );
  }
  if (slot < 0 && !allowedRanges.includes(range)) {
    throw new RangeError(
      `firingTrig: unsupported (range, m) = (${String(range)}, ${String(m)}); regen FIRING_ARC.`,
    );
  }
  if (slot < 0) {
    // Preserve shipped arc literals exactly. Custom ranges use a fixed
    // IEEE operation sequence, so browsers never evaluate platform trig.
    const pi = 3.141592653589793;
    let angle = ((m * 24) / (range - 8)) % (2 * pi);
    if (angle > pi) angle -= 2 * pi;
    const squared = angle * angle;
    let sinTerm = angle,
      cosTerm = 1,
      sin = angle,
      cos = 1;
    for (let k = 1; k <= 12; k++) {
      sinTerm *= -squared / (2 * k * (2 * k + 1));
      cosTerm *= -squared / ((2 * k - 1) * (2 * k));
      sin += sinTerm;
      cos += cosTerm;
    }
    return { sin: m === 0 ? 0 : sgn * sin, cos };
  }
  const pair = FIRING_ARC[slot * 17 + m]!;
  return { sin: m === 0 ? 0 : sgn * pair[0], cos: pair[1] };
}
